import {
  GAMEPAD_AXES,
  GAMEPAD_BINDINGS,
  INPUT_ACTIONS,
  KEY_BINDINGS,
  POINTER_BINDINGS,
} from './InputBindings.js';

const clamp = (value, min = -1, max = 1) => Math.min(max, Math.max(min, value));
const hasAnyKey = (keys, bindings) => bindings.some((code) => keys.has(code));
const finiteOr = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;

const DEFAULT_SOURCE_POLICY = Object.freeze({
  keyboard: true,
  pointer: true,
  touch: true,
  gamepad: 'first',
});

function applyDeadzone(value, deadzone) {
  const magnitude = Math.abs(value);
  if (magnitude <= deadzone) return 0;
  const normalized = (magnitude - deadzone) / (1 - deadzone);
  return Math.sign(value) * clamp(normalized, 0, 1);
}

function neutralActions() {
  return Object.fromEntries(INPUT_ACTIONS.map((name) => [name, {
    down: false,
    pressed: false,
    released: false,
  }]));
}

function neutralSnapshot() {
  return {
    version: 1,
    sequence: 0,
    timestamp: 0,
    move: { x: 0, y: 0 },
    look: { x: 0, y: 0 },
    zoom: 0,
    actions: neutralActions(),
    sources: {
      keyboard: false,
      pointer: false,
      touch: false,
      gamepad: null,
      virtual: false,
    },
  };
}

function normalizeGamepadPolicy(value) {
  if (value === 'first' || value === null) return value;
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError('sourcePolicy.gamepad must be first, null, or a non-negative gamepad index');
  }
  return value;
}

export class InputManager {
  constructor({ navigatorRef = globalThis.navigator, gamepadDeadzone = 0.18 } = {}) {
    this.navigatorRef = navigatorRef;
    this.gamepadDeadzone = gamepadDeadzone;
    this.keys = new Set();
    this.pointerButtons = new Set();
    this.pointerDelta = { x: 0, y: 0 };
    this.wheelDelta = 0;
    this.touches = new Map();
    this.touchLookDelta = { x: 0, y: 0 };
    this.sources = new Map();
    this.virtualState = null;
    this.previousActions = Object.fromEntries(INPUT_ACTIONS.map((name) => [name, false]));
    this.lastSnapshot = neutralSnapshot();
    this.sequence = 0;
    this.sourcePolicy = { ...DEFAULT_SOURCE_POLICY };
  }

  attach({ windowRef = globalThis.window, documentRef = windowRef?.document } = {}) {
    const eventTarget = documentRef || windowRef;
    if (!eventTarget?.addEventListener || !windowRef?.addEventListener) return () => {};
    if (this.sources.has(eventTarget)) return this.sources.get(eventTarget).detach;

    const onKeyDown = (event) => {
      if (event.code) this.keys.add(event.code);
    };
    const onKeyUp = (event) => {
      if (event.code) this.keys.delete(event.code);
    };
    const onPointerDown = (event) => {
      if (Number.isInteger(event.button)) this.pointerButtons.add(event.button);
    };
    const onPointerUp = (event) => {
      if (Number.isInteger(event.button)) this.pointerButtons.delete(event.button);
    };
    const onPointerMove = (event) => {
      this.pointerDelta.x += finiteOr(event.movementX);
      this.pointerDelta.y += finiteOr(event.movementY);
    };
    const onWheel = (event) => {
      this.wheelDelta += finiteOr(event.deltaY);
    };
    const onTouchStart = (event) => {
      for (const touch of Array.from(event.changedTouches || [])) {
        this.touches.set(touch.identifier, {
          originX: touch.clientX,
          originY: touch.clientY,
          x: touch.clientX,
          y: touch.clientY,
          lastX: touch.clientX,
          lastY: touch.clientY,
        });
      }
    };
    const onTouchMove = (event) => {
      for (const touch of Array.from(event.changedTouches || [])) {
        const tracked = this.touches.get(touch.identifier);
        if (!tracked) continue;
        const touchIndex = Array.from(this.touches.keys()).indexOf(touch.identifier);
        if (touchIndex > 0) {
          this.touchLookDelta.x += touch.clientX - tracked.lastX;
          this.touchLookDelta.y += touch.clientY - tracked.lastY;
        }
        tracked.lastX = touch.clientX;
        tracked.lastY = touch.clientY;
        tracked.x = touch.clientX;
        tracked.y = touch.clientY;
      }
    };
    const onTouchEnd = (event) => {
      for (const touch of Array.from(event.changedTouches || [])) {
        this.touches.delete(touch.identifier);
      }
    };
    const onBlur = () => this.resetPhysicalState();

    eventTarget.addEventListener('keydown', onKeyDown);
    eventTarget.addEventListener('keyup', onKeyUp);
    eventTarget.addEventListener('pointerdown', onPointerDown);
    eventTarget.addEventListener('pointerup', onPointerUp);
    eventTarget.addEventListener('pointercancel', onPointerUp);
    eventTarget.addEventListener('pointermove', onPointerMove);
    eventTarget.addEventListener('wheel', onWheel, { passive: true });
    eventTarget.addEventListener('touchstart', onTouchStart, { passive: true });
    eventTarget.addEventListener('touchmove', onTouchMove, { passive: true });
    eventTarget.addEventListener('touchend', onTouchEnd);
    eventTarget.addEventListener('touchcancel', onTouchEnd);
    windowRef.addEventListener('blur', onBlur);

    const detach = () => {
      eventTarget.removeEventListener('keydown', onKeyDown);
      eventTarget.removeEventListener('keyup', onKeyUp);
      eventTarget.removeEventListener('pointerdown', onPointerDown);
      eventTarget.removeEventListener('pointerup', onPointerUp);
      eventTarget.removeEventListener('pointercancel', onPointerUp);
      eventTarget.removeEventListener('pointermove', onPointerMove);
      eventTarget.removeEventListener('wheel', onWheel);
      eventTarget.removeEventListener('touchstart', onTouchStart);
      eventTarget.removeEventListener('touchmove', onTouchMove);
      eventTarget.removeEventListener('touchend', onTouchEnd);
      eventTarget.removeEventListener('touchcancel', onTouchEnd);
      windowRef.removeEventListener('blur', onBlur);
      this.sources.delete(eventTarget);
    };

    this.sources.set(eventTarget, { detach });
    return detach;
  }

  detachAll() {
    for (const source of [...this.sources.values()]) source.detach();
    this.resetPhysicalState();
  }

  setSourcePolicy(options = {}) {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      throw new TypeError('sourcePolicy must be an object');
    }
    this.sourcePolicy = {
      keyboard: options.keyboard === undefined
        ? this.sourcePolicy.keyboard
        : Boolean(options.keyboard),
      pointer: options.pointer === undefined
        ? this.sourcePolicy.pointer
        : Boolean(options.pointer),
      touch: options.touch === undefined
        ? this.sourcePolicy.touch
        : Boolean(options.touch),
      gamepad: options.gamepad === undefined
        ? this.sourcePolicy.gamepad
        : normalizeGamepadPolicy(options.gamepad),
    };
    return this.getSourcePolicy();
  }

  resetSourcePolicy() {
    this.sourcePolicy = { ...DEFAULT_SOURCE_POLICY };
    return this.getSourcePolicy();
  }

  getSourcePolicy() {
    return Object.freeze({ ...this.sourcePolicy });
  }

  setVirtualInput(state = {}) {
    this.virtualState = {
      move: state.move ? {
        x: clamp(finiteOr(state.move.x)),
        y: clamp(finiteOr(state.move.y)),
      } : undefined,
      look: state.look ? {
        x: clamp(finiteOr(state.look.x)),
        y: clamp(finiteOr(state.look.y)),
      } : undefined,
      zoom: Number.isFinite(state.zoom) ? clamp(state.zoom) : undefined,
      actions: state.actions ? { ...state.actions } : undefined,
    };
    return this.virtualState;
  }

  clearVirtualInput() {
    this.virtualState = null;
  }

  resetPhysicalState() {
    this.keys.clear();
    this.pointerButtons.clear();
    this.pointerDelta.x = 0;
    this.pointerDelta.y = 0;
    this.wheelDelta = 0;
    this.touches.clear();
    this.touchLookDelta.x = 0;
    this.touchLookDelta.y = 0;
  }

  reset() {
    this.resetPhysicalState();
    this.clearVirtualInput();
    this.previousActions = Object.fromEntries(INPUT_ACTIONS.map((name) => [name, false]));
    this.lastSnapshot = neutralSnapshot();
    this.sequence = 0;
    return this.getSnapshot();
  }

  getSnapshot() {
    return this.lastSnapshot;
  }

  sample(timestamp = globalThis.performance?.now?.() ?? Date.now()) {
    const gamepad = this.#pollGamepad();
    const touch = this.sourcePolicy.touch ? this.#readTouch() : { move: { x: 0, y: 0 } };
    const keyboardMove = this.sourcePolicy.keyboard ? {
      x: Number(hasAnyKey(this.keys, KEY_BINDINGS.moveRight)) - Number(hasAnyKey(this.keys, KEY_BINDINGS.moveLeft)),
      y: Number(hasAnyKey(this.keys, KEY_BINDINGS.moveForward)) - Number(hasAnyKey(this.keys, KEY_BINDINGS.moveBackward)),
    } : { x: 0, y: 0 };

    const physicalMove = {
      x: clamp(keyboardMove.x + finiteOr(gamepad?.move.x) + touch.move.x),
      y: clamp(keyboardMove.y + finiteOr(gamepad?.move.y) + touch.move.y),
    };
    const physicalLook = {
      x: clamp((this.sourcePolicy.pointer ? this.pointerDelta.x * 0.04 : 0)
        + finiteOr(gamepad?.look.x)
        + (this.sourcePolicy.touch ? this.touchLookDelta.x * 0.02 : 0)),
      y: clamp((this.sourcePolicy.pointer ? this.pointerDelta.y * 0.04 : 0)
        + finiteOr(gamepad?.look.y)
        + (this.sourcePolicy.touch ? this.touchLookDelta.y * 0.02 : 0)),
    };

    const move = this.virtualState?.move || physicalMove;
    const look = this.virtualState?.look || physicalLook;
    const zoom = this.virtualState?.zoom
      ?? (this.sourcePolicy.pointer ? clamp(this.wheelDelta / 120) : 0);
    const actionStates = {};

    for (const action of INPUT_ACTIONS) {
      const physicalDown = this.#isActionDown(action, gamepad);
      const virtualDown = Boolean(this.virtualState?.actions?.[action]);
      const down = physicalDown || virtualDown;
      const previous = this.previousActions[action];
      actionStates[action] = {
        down,
        pressed: down && !previous,
        released: !down && previous,
      };
      this.previousActions[action] = down;
    }

    this.lastSnapshot = {
      version: 1,
      sequence: ++this.sequence,
      timestamp,
      move: { x: clamp(move.x), y: clamp(move.y) },
      look: { x: clamp(look.x), y: clamp(look.y) },
      zoom,
      actions: actionStates,
      sources: {
        keyboard: this.sourcePolicy.keyboard && this.keys.size > 0,
        pointer: this.sourcePolicy.pointer
          && (this.pointerButtons.size > 0 || this.pointerDelta.x !== 0 || this.pointerDelta.y !== 0),
        touch: this.sourcePolicy.touch && this.touches.size > 0,
        gamepad: gamepad ? { index: gamepad.index, id: gamepad.id } : null,
        virtual: Boolean(this.virtualState),
      },
    };

    this.pointerDelta.x = 0;
    this.pointerDelta.y = 0;
    this.touchLookDelta.x = 0;
    this.touchLookDelta.y = 0;
    this.wheelDelta = 0;

    return this.lastSnapshot;
  }

  #readTouch() {
    const primaryTouch = this.touches.values().next().value;
    if (!primaryTouch) return { move: { x: 0, y: 0 } };
    return {
      move: {
        x: clamp((primaryTouch.x - primaryTouch.originX) / 64),
        y: clamp((primaryTouch.originY - primaryTouch.y) / 64),
      },
    };
  }

  #isActionDown(action, gamepad) {
    if (this.sourcePolicy.keyboard
      && KEY_BINDINGS[action]
      && hasAnyKey(this.keys, KEY_BINDINGS[action])) return true;
    if (this.sourcePolicy.pointer
      && POINTER_BINDINGS[action] !== undefined
      && this.pointerButtons.has(POINTER_BINDINGS[action])) return true;
    const buttonIndex = GAMEPAD_BINDINGS[action];
    return buttonIndex !== undefined && Boolean(gamepad?.buttons?.[buttonIndex]);
  }

  #pollGamepad() {
    if (this.sourcePolicy.gamepad === null) return null;
    let pads;
    try {
      pads = this.navigatorRef?.getGamepads?.();
    } catch {
      return null;
    }
    const candidates = Array.from(pads || []).filter((candidate) => candidate?.connected !== false);
    const pad = this.sourcePolicy.gamepad === 'first'
      ? candidates[0]
      : candidates.find((candidate) => candidate?.index === this.sourcePolicy.gamepad);
    if (!pad) return null;

    const axis = (index) => applyDeadzone(finiteOr(pad.axes?.[index]), this.gamepadDeadzone);
    return {
      index: pad.index ?? 0,
      id: pad.id || 'Gamepad',
      move: {
        x: axis(GAMEPAD_AXES.moveX),
        y: -axis(GAMEPAD_AXES.moveY),
      },
      look: {
        x: axis(GAMEPAD_AXES.lookX),
        y: axis(GAMEPAD_AXES.lookY),
      },
      buttons: Array.from(pad.buttons || [], (button) => Boolean(button?.pressed || button?.value > 0.5)),
    };
  }
}
